import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import { Badge, DataTable, type TanstackReactTable } from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { ClassEnrollmentListItemDTO } from "../api/client";
import {
  CLASS_ENROLLMENT_STATUS_LABELS,
  CLASS_ENROLLMENT_STATUS_VARIANTS,
} from "../lib/status";

type Props = {
  isLoading: boolean;
  data: ClassEnrollmentListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (id: string) => void;
};

export function ClassEnrollmentsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  onView,
}: Props) {
  const columns: TanstackReactTable.ColumnDef<ClassEnrollmentListItemDTO>[] = [
    {
      id: "submittedAt",
      header: "ວັນທີສະໝັກ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.submittedAt)}
        </span>
      ),
    },
    {
      id: "fullName",
      header: "ຜູ້ສະໝັກ",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.fullName}</p>
          {row.original.nickname ? (
            <p className="text-muted-foreground text-xs">{row.original.nickname}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: "phoneNumber",
      header: "ເບີໂທ",
      cell: ({ row }) => row.original.phoneNumber,
    },
    {
      id: "classes",
      header: "ຄລາດສ໌",
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.classes.map((item) => (
            <Badge key={item.templateId} variant="secondary" className="text-xs">
              {item.classTypeName} {item.levelName}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge variant={CLASS_ENROLLMENT_STATUS_VARIANTS[row.original.status] ?? "outline"}>
          {CLASS_ENROLLMENT_STATUS_LABELS[row.original.status] ?? row.original.status}
        </Badge>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<ClassEnrollmentListItemDTO, unknown>
      noDataMessage="ຍັງບໍ່ມີໃບສະໝັກຮຽນ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(page) => onPaginationChange(page.offset, page.limit)}
    />
  );
}
